import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import "./importInventoryModal.scss";
import Loader from "../../common/Loader";
import { ApiGet, ApiPost, ApiPut } from "../../../helpers/API/ApiData";
import Success from "../../Toaster/Success/Success";
import { postImportAuditLog } from "../../../utils/importAuditLog.util";
import {
  buildHeaderMap,
  canonicalizeDisplayName,
  classifyInventoryRows,
  computeFinalProductName,
  extractApiErrorMessage,
  normalizeName,
  normalizeLooseName,
} from "../../../utils/inventoryImport.util";

export default function ImportInventoryModal({
  toggle,
  companyId,
  businessName,
  onImportComplete,
}) {
  const fileRef = useRef(null);
  const brandMapRef = useRef({});
  const categoryMapRef = useRef({});
  const brandInFlightRef = useRef({});
  const categoryInFlightRef = useRef({});
  const closeTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const cancelImportRef = useRef(false);
  const logsRef = useRef([]);
  const flushTimerRef = useRef(null);
  const countersRef = useRef({
    processed: 0,
    success: 0,
    failed: 0,
    skipped: 0,
    created: 0,
    updated: 0,
  });
  const progressFlushTimerRef = useRef(null);
  const existingProductByNameRef = useRef({});

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toastmsg, setToastmsg] = useState("");
  const [er, setEr] = useState("");

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [headerMap, setHeaderMap] = useState({});
  const [detectedHeaders, setDetectedHeaders] = useState([]);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState("upload"); // upload | preview | importing

  const [autoCreateMissing, setAutoCreateMissing] = useState(true);
  const [prefixBrandToName, setPrefixBrandToName] = useState(true);

  const [apiPaths, setApiPaths] = useState({
    brandList: null,
    brandCreate: null,
    categoryList: null,
    categoryCreate: null,
    productCreate: "product",
  });

  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    failed: 0,
    skipped: 0,
    created: 0,
    updated: 0,
    running: false,
  });
  const [logs, setLogs] = useState([]);

  const totalRows = rows?.length || 0;

  const canValidate = useMemo(() => {
    return (
      !!companyId &&
      totalRows > 0 &&
      !!headerMap?.productName &&
      !!headerMap?.brand &&
      !!headerMap?.category &&
      !!headerMap?.price &&
      !!headerMap?.quantity &&
      !progress.running
    );
  }, [companyId, headerMap, progress.running, totalRows]);

  const canConfirm =
    canValidate &&
    preview &&
    (preview.summary?.toCreate > 0 || preview.summary?.toUpdate > 0) &&
    step === "preview" &&
    !progress.running;

  const loadExistingProducts = useCallback(async () => {
    const map = {};
    try {
      const productsRes = await ApiGet(`product/company/${companyId}`);
      const list = productsRes?.data?.data || [];
      for (const p of list) {
        if (p?.isActive === false) continue;
        const nameKey = normalizeName(p?.productName || "");
        const id = p?._id || p?.id;
        if (nameKey && id) map[nameKey] = { id, name: p.productName, product: p };
      }
    } catch (e) {
      // continue without map
    }
    existingProductByNameRef.current = map;
    return map;
  }, [companyId]);

  const rebuildPreview = useCallback(
    (productMap) => {
      if (!rows?.length || !headerMap?.productName) {
        setPreview(null);
        return null;
      }
      const result = classifyInventoryRows({
        rows,
        headerMap,
        prefixBrandToName,
        autoCreateMissing,
        existingProductByName: productMap || existingProductByNameRef.current,
        brandMap: brandMapRef.current,
        categoryMap: categoryMapRef.current,
      });
      setPreview(result);
      setStep("preview");
      return result;
    },
    [rows, headerMap, prefixBrandToName, autoCreateMissing]
  );

  useEffect(() => {
    let mounted = true;
    const fetchLists = async () => {
      if (!companyId) return;
      setIsLoading(true);
      try {
        const tryGetFirst = async (paths) => {
          let lastErr;
          for (const p of paths) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const res = await ApiGet(p);
              return { path: p, res };
            } catch (e) {
              lastErr = e;
            }
          }
          throw lastErr || new Error("No working endpoint found");
        };

        const brandListCandidates = [
          `ibrand/company/${companyId}`,
          `ibrand/${companyId}`,
          `ibrand?companyId=${companyId}`,
          `ibrand`,
          `brand/company/${companyId}`,
          `brands/company/${companyId}`,
        ];

        const categoryListCandidates = [
          `icategory/company/${companyId}`,
          `icategory/${companyId}`,
          `icategory?companyId=${companyId}`,
          `icategory`,
        ];

        let brands = [];
        let categories = [];
        let brandListPath = null;
        let categoryListPath = null;

        try {
          const r = await tryGetFirst(brandListCandidates);
          brandListPath = r.path;
          brands = r.res?.data?.data || r.res?.data?.data?.data || [];
        } catch (e) {
          brands = [];
        }

        try {
          const r = await tryGetFirst(categoryListCandidates);
          categoryListPath = r.path;
          categories = r.res?.data?.data || r.res?.data?.data?.data || [];
        } catch (e) {
          categories = [];
        }

        const nextBrandMap = {};
        (brands || []).forEach((b) => {
          const name = b?.brandName || b?.name || b?.BrandName || b?.title || "";
          const id = b?._id || b?.id || b?.brandId || b?.BrandId;
          const keyStrict = normalizeName(name);
          const keyLoose = normalizeLooseName(name);
          if (id) {
            if (keyStrict) nextBrandMap[keyStrict] = { id, name };
            if (keyLoose) nextBrandMap[keyLoose] = { id, name };
          }
        });

        const nextCategoryMap = {};
        (categories || []).forEach((c) => {
          const name =
            c?.categoryName || c?.name || c?.CategoryName || c?.title || "";
          const id = c?._id || c?.id || c?.categoryId || c?.CategoryId;
          const keyStrict = normalizeName(name);
          const keyLoose = normalizeLooseName(name);
          if (id) {
            if (keyStrict) nextCategoryMap[keyStrict] = { id, name };
            if (keyLoose) nextCategoryMap[keyLoose] = { id, name };
          }
        });

        if (!mounted) return;
        brandMapRef.current = nextBrandMap;
        categoryMapRef.current = nextCategoryMap;
        setApiPaths((prev) => ({
          ...prev,
          brandList: brandListPath,
          categoryList: categoryListPath,
        }));
        await loadExistingProducts();
      } catch (e) {
        console.log("Failed to fetch brand/category lists", e);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchLists();
    return () => {
      mounted = false;
      mountedRef.current = false;
      cancelImportRef.current = true;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, [companyId, loadExistingProducts]);

  useEffect(() => {
    if (!success) return;
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setSuccess(false);
      setEr("");
      setToastmsg("");
    }, 5000);
  }, [success, er]);

  // Reclassify when options change after a preview exists
  useEffect(() => {
    if (step !== "preview" || !rows.length) return;
    rebuildPreview(existingProductByNameRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreateMissing, prefixBrandToName]);

  const scheduleFlushLogs = () => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      if (!mountedRef.current) return;
      setLogs(logsRef.current.slice(-200));
    }, 100);
  };

  const addLog = (entry) => {
    logsRef.current.push({ ts: Date.now(), ...entry });
    scheduleFlushLogs();
  };

  const scheduleFlushProgress = () => {
    if (progressFlushTimerRef.current) return;
    progressFlushTimerRef.current = setTimeout(() => {
      progressFlushTimerRef.current = null;
      if (!mountedRef.current) return;
      setProgress((p) => ({
        ...p,
        processed: countersRef.current.processed,
        success: countersRef.current.success,
        failed: countersRef.current.failed,
        skipped: countersRef.current.skipped,
        created: countersRef.current.created,
        updated: countersRef.current.updated,
      }));
    }, 120);
  };

  const bumpCounters = (delta) => {
    if (delta.processed) countersRef.current.processed += delta.processed;
    if (delta.success) countersRef.current.success += delta.success;
    if (delta.failed) countersRef.current.failed += delta.failed;
    if (delta.skipped) countersRef.current.skipped += delta.skipped;
    if (delta.created) countersRef.current.created += delta.created;
    if (delta.updated) countersRef.current.updated += delta.updated;
    scheduleFlushProgress();
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const apiPostWithRetry = async (path, payload, attempts = 4) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await ApiPost(path, payload);
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        const shouldRetry =
          status === 429 ||
          status === 502 ||
          status === 503 ||
          status === 504 ||
          !status;
        if (!shouldRetry) throw e;
        // eslint-disable-next-line no-await-in-loop
        await sleep(
          Math.min(1500, 250 * Math.pow(2, i)) + Math.floor(Math.random() * 120)
        );
      }
    }
    throw lastErr;
  };

  const apiPutWithRetry = async (path, payload, attempts = 4) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await ApiPut(path, payload);
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        const shouldRetry =
          status === 429 ||
          status === 502 ||
          status === 503 ||
          status === 504 ||
          !status;
        if (!shouldRetry) throw e;
        // eslint-disable-next-line no-await-in-loop
        await sleep(
          Math.min(1500, 250 * Math.pow(2, i)) + Math.floor(Math.random() * 120)
        );
      }
    }
    throw lastErr;
  };

  const runPool = async (items, concurrency, handler) => {
    let idx = 0;
    const workers = new Array(Math.max(1, concurrency)).fill(null).map(async () => {
      while (idx < items.length) {
        if (cancelImportRef.current || !mountedRef.current) return;
        const currentIdx = idx++;
        // eslint-disable-next-line no-await-in-loop
        await handler(items[currentIdx], currentIdx);
      }
    });
    await Promise.all(workers);
  };

  const resetStateForNewFile = () => {
    setRows([]);
    setHeaderMap({});
    setDetectedHeaders([]);
    setPreview(null);
    setStep("upload");
    setLogs([]);
    logsRef.current = [];
    countersRef.current = {
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
    };
    setProgress({
      total: 0,
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
      running: false,
    });
  };

  const onPickFile = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const onFileChange = async (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    resetStateForNewFile();
    setFileName(f.name);

    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["xls", "xlsx"].includes(ext)) {
      setSuccess(true);
      setToastmsg("Please upload a .xls or .xlsx file");
      setEr("error");
      return;
    }

    setIsLoading(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("No sheet found in file");
      const ws = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(ws, {
        defval: "",
        raw: false,
      });

      const headers = json?.length > 0 ? Object.keys(json[0] || {}) : [];
      const hMap = buildHeaderMap(headers);

      setDetectedHeaders(headers);
      setHeaderMap(hMap);
      setRows(json);

      if (
        !hMap.productName ||
        !hMap.brand ||
        !hMap.category ||
        !hMap.price ||
        !hMap.quantity
      ) {
        addLog({
          type: "error",
          rowNumber: 0,
          message:
            "Missing required columns. Required: Product Name, Brand, Category, Price, Quantity.",
        });
      } else {
        const productMap = await loadExistingProducts();
        const result = classifyInventoryRows({
          rows: json,
          headerMap: hMap,
          prefixBrandToName,
          autoCreateMissing,
          existingProductByName: productMap,
          brandMap: brandMapRef.current,
          categoryMap: categoryMapRef.current,
        });
        setPreview(result);
        setStep("preview");
      }
    } catch (err) {
      setSuccess(true);
      setToastmsg(extractApiErrorMessage(err));
      setEr("error");
    } finally {
      setIsLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onValidateClick = async () => {
    if (!canValidate) return;
    setIsLoading(true);
    try {
      const productMap = await loadExistingProducts();
      rebuildPreview(productMap);
      setSuccess(true);
      setToastmsg("Validation complete — review summary then Confirm import");
      setEr("");
    } catch (e) {
      setSuccess(true);
      setToastmsg(extractApiErrorMessage(e));
      setEr("error");
    } finally {
      setIsLoading(false);
    }
  };

  const resolveOrCreateBrand = async (brandName) => {
    const keyStrict = normalizeName(brandName);
    const keyLoose = normalizeLooseName(brandName);
    const existing =
      brandMapRef.current[keyStrict] || brandMapRef.current[keyLoose];
    if (existing?.id) return existing;
    if (!autoCreateMissing) throw new Error(`Brand not found: ${brandName}`);

    const lockKey = keyLoose || keyStrict;
    if (lockKey && brandInFlightRef.current[lockKey]) {
      return await brandInFlightRef.current[lockKey];
    }

    const canonicalName = canonicalizeDisplayName(brandName);
    const payload = { brandName: canonicalName, name: canonicalName, companyId };
    const candidates = [
      apiPaths.brandCreate,
      "ibrand",
      "brand",
      "brands",
    ].filter(Boolean);

    const creatorPromise = (async () => {
      let res;
      let lastErr;
      for (const p of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          res = await apiPostWithRetry(p, payload);
          setApiPaths((prev) => ({ ...prev, brandCreate: prev.brandCreate || p }));
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!res) throw lastErr || new Error("Brand create failed");

      const created = res?.data?.data || res?.data?.data?.data || res?.data;
      const id =
        created?._id ||
        created?.id ||
        created?.brandId ||
        created?.data?._id ||
        created?.data?.id;
      const name =
        created?.brandName || created?.name || created?.BrandName || payload.brandName;

      const next = { id, name };
      brandMapRef.current = {
        ...brandMapRef.current,
        [keyStrict]: next,
        [keyLoose]: next,
      };
      return next;
    })();

    if (lockKey) brandInFlightRef.current[lockKey] = creatorPromise;
    try {
      return await creatorPromise;
    } finally {
      if (lockKey) delete brandInFlightRef.current[lockKey];
    }
  };

  const resolveOrCreateCategory = async (categoryName) => {
    const keyStrict = normalizeName(categoryName);
    const keyLoose = normalizeLooseName(categoryName);
    const existing =
      categoryMapRef.current[keyStrict] || categoryMapRef.current[keyLoose];
    if (existing?.id) return existing;
    if (!autoCreateMissing)
      throw new Error(`Category not found: ${categoryName}`);

    const lockKey = keyLoose || keyStrict;
    if (lockKey && categoryInFlightRef.current[lockKey]) {
      return await categoryInFlightRef.current[lockKey];
    }

    const canonicalName = canonicalizeDisplayName(categoryName);
    const payload = {
      categoryName: canonicalName,
      name: canonicalName,
      companyId,
    };
    const candidates = [
      apiPaths.categoryCreate,
      "icategory",
      "category",
      "categories",
    ].filter(Boolean);

    const creatorPromise = (async () => {
      let res;
      let lastErr;
      for (const p of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          res = await apiPostWithRetry(p, payload);
          setApiPaths((prev) => ({
            ...prev,
            categoryCreate: prev.categoryCreate || p,
          }));
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!res) throw lastErr || new Error("Category create failed");

      const created = res?.data?.data || res?.data?.data?.data || res?.data;
      const id =
        created?._id ||
        created?.id ||
        created?.categoryId ||
        created?.data?._id ||
        created?.data?.id;
      const name =
        created?.categoryName ||
        created?.name ||
        created?.CategoryName ||
        payload.categoryName;

      const next = { id, name };
      categoryMapRef.current = {
        ...categoryMapRef.current,
        [keyStrict]: next,
        [keyLoose]: next,
      };
      return next;
    })();

    if (lockKey) categoryInFlightRef.current[lockKey] = creatorPromise;
    try {
      return await creatorPromise;
    } finally {
      if (lockKey) delete categoryInFlightRef.current[lockKey];
    }
  };

  const ensureStockInitialized = async ({
    productId,
    retailInitialStock,
    storeInitialStock,
  }) => {
    if (!productId) throw new Error("Missing productId for stock init");

    const retailStock = Number.isFinite(retailInitialStock)
      ? retailInitialStock
      : 0;
    const storeStock = Number.isFinite(storeInitialStock) ? storeInitialStock : 0;

    const postCandidates = ["stock/", "stock"];
    const payload = {
      productId,
      retailInitialStock: String(retailStock),
      storeInitialStock: String(storeStock),
      type: "CR",
      companyId,
    };

    let lastErr;
    for (const p of postCandidates) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await apiPostWithRetry(p, payload);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status && status !== 404) throw e;
        lastErr = e;
      }
    }
    throw lastErr || new Error("Stock init endpoint not found");
  };

  const createProduct = async (rowData) => {
    const retailPriceStr = String(rowData.price ?? "").trim();
    const quantityStr = String(rowData.quantityPerUnit ?? "").trim();
    const retailInitialStockStr = String(rowData.retailInitialStock ?? 0);
    const storeInitialStockStr = String(rowData.storeInitialStock ?? 0);

    const payload = {
      productType: rowData.productType || "Retail",
      unit: rowData.unit || undefined,
      taxType: "Inclusive",
      discountType: "₹",
      retailPrice: retailPriceStr,
      discount: "",
      discountPrice: retailPriceStr,
      productName: rowData.productName,
      quantity: quantityStr,
      retailInitialStock: retailInitialStockStr,
      storeInitialStock: storeInitialStockStr,
      hsn: rowData.hsn || "",
      categoryId: rowData.categoryId,
      brandId: rowData.brandId,
      tax: [],
      totalTax: 0,
      companyId,
      isActive: true,
      vendorId: [],
    };
    const candidates = [
      apiPaths.productCreate,
      "product",
      "products",
    ].filter(Boolean);

    let res;
    let lastErr;
    for (const p of candidates) {
      try {
        // eslint-disable-next-line no-await-in-loop
        res = await apiPostWithRetry(p, payload);
        setApiPaths((prev) => ({
          ...prev,
          productCreate: prev.productCreate || p,
        }));
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (!res) throw lastErr || new Error("Product create failed");
    return res;
  };

  const updateProduct = async (existingId, rowData) => {
    const retailPriceStr = String(rowData.price ?? "").trim();
    const quantityStr = String(rowData.quantityPerUnit ?? "").trim();
    const payload = {
      productName: rowData.productName,
      retailPrice: retailPriceStr,
      discountPrice: retailPriceStr,
      quantity: quantityStr,
      categoryId: rowData.categoryId,
      brandId: rowData.brandId,
      companyId,
    };
    if (rowData.productType) payload.productType = rowData.productType;
    if (rowData.unit) payload.unit = rowData.unit;
    if (rowData.hsn !== undefined) payload.hsn = rowData.hsn || "";
    return apiPutWithRetry(`product/${existingId}`, payload);
  };

  const startImport = async () => {
    if (!canConfirm && !(preview && (preview.summary?.toCreate || preview.summary?.toUpdate))) {
      if (canValidate && !preview) {
        await onValidateClick();
        return;
      }
      return;
    }

    const plan =
      preview ||
      classifyInventoryRows({
        rows,
        headerMap,
        prefixBrandToName,
        autoCreateMissing,
        existingProductByName: existingProductByNameRef.current,
        brandMap: brandMapRef.current,
        categoryMap: categoryMapRef.current,
      });

    const actionable = (plan.planRows || []).filter(
      (r) => r.action === "create" || r.action === "update"
    );
    if (!actionable.length) {
      setSuccess(true);
      setToastmsg("Nothing to import — fix errors or choose another file");
      setEr("error");
      return;
    }

    logsRef.current = [];
    setLogs([]);
    cancelImportRef.current = false;
    countersRef.current = {
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
    };
    setStep("importing");
    setProgress({
      total: actionable.length,
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      created: 0,
      updated: 0,
      running: true,
    });

    // Log preview errors as skipped
    (plan.planRows || [])
      .filter((r) => r.action === "error")
      .forEach((r) => {
        addLog({
          type: "skip",
          action: "error",
          rowNumber: r.excelRowNumber,
          name: r.finalName || r.productName,
          message: r.error || "Invalid row",
        });
        bumpCounters({ skipped: 1 });
      });

    const CHUNK_SIZE = 50;
    const CONCURRENCY = 1;

    for (let start = 0; start < actionable.length; start += CHUNK_SIZE) {
      if (cancelImportRef.current || !mountedRef.current) break;
      const chunk = actionable.slice(start, start + CHUNK_SIZE);

      // eslint-disable-next-line no-await-in-loop
      await runPool(chunk, CONCURRENCY, async (planRow) => {
        if (cancelImportRef.current || !mountedRef.current) return;
        const { parsed, excelRowNumber, action, existingId, finalName } = planRow;
        try {
          if (!parsed) throw new Error("Missing parsed row");

          const brand = await resolveOrCreateBrand(parsed.brand);
          const category = await resolveOrCreateCategory(parsed.category);
          if (!brand?.id) throw new Error(`Brand create/resolve failed`);
          if (!category?.id) throw new Error(`Category create/resolve failed`);

          const name = finalName ||
            computeFinalProductName({
              brandName: brand.name || parsed.brand,
              productName: parsed.productName,
              prefixBrand: prefixBrandToName,
            });

          const rowData = {
            productName: name,
            brandId: brand.id,
            categoryId: category.id,
            price: parsed.price,
            quantityPerUnit: parsed.quantity,
            productType: parsed.productType,
            unit: parsed.unit,
            hsn: parsed.hsn,
            retailInitialStock: parsed.retailInitialStock,
            storeInitialStock: parsed.storeInitialStock,
          };

          if (action === "update" && existingId) {
            await updateProduct(existingId, rowData);
            addLog({
              type: "success",
              action: "update",
              rowNumber: excelRowNumber,
              name,
              message: `Updated: ${name}`,
            });
            bumpCounters({ success: 1, updated: 1, processed: 1 });
          } else {
            const createdRes = await createProduct(rowData);
            try {
              const createdProduct =
                createdRes?.data?.data ||
                createdRes?.data?.data?.data ||
                createdRes?.data ||
                {};
              const productId = createdProduct?._id || createdProduct?.id;
              await ensureStockInitialized({
                productId,
                retailInitialStock: parsed.retailInitialStock,
                storeInitialStock: parsed.storeInitialStock,
              });
            } catch (e) {
              // stock init best-effort
            }
            addLog({
              type: "success",
              action: "create",
              rowNumber: excelRowNumber,
              name,
              message: `Created: ${name}`,
            });
            bumpCounters({ success: 1, created: 1, processed: 1 });
          }
        } catch (err) {
          addLog({
            type: "error",
            action: "error",
            rowNumber: excelRowNumber,
            name: finalName || parsed?.productName,
            message: extractApiErrorMessage(err),
          });
          bumpCounters({ failed: 1, processed: 1 });
        }
      });

      // eslint-disable-next-line no-await-in-loop
      await sleep(150);
    }

    if (!mountedRef.current) return;
    scheduleFlushProgress();
    setProgress((p) => ({ ...p, running: false }));
    setStep("preview");

    const createdCount = countersRef.current.created;
    const updatedCount = countersRef.current.updated;
    const failedCount = countersRef.current.failed;
    const skippedCount = countersRef.current.skipped;
    const successCount = countersRef.current.success;

    const logResult = await postImportAuditLog({
      companyId,
      businessName,
      importType: "inventory",
      fileName,
      totalRecords: totalRows,
      successCount,
      failedCount,
      createdCount,
      updatedCount,
      skippedCount,
      logs: logsRef.current,
    });

    setSuccess(true);
    if (failedCount === 0) {
      setToastmsg(
        `Import completed. Created: ${createdCount}, Updated: ${updatedCount}`
      );
      setEr("");
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        if (typeof onImportComplete === "function") onImportComplete();
        toggle();
      }, 2500);
    } else {
      setToastmsg(
        `Import finished. Created: ${createdCount}, Updated: ${updatedCount}, Failed: ${failedCount}, Skipped: ${skippedCount}`
      );
      setEr("error");
    }

    if (!logResult.ok) {
      setTimeout(() => {
        if (!mountedRef.current) return;
        setSuccess(true);
        setToastmsg("Import done, but audit log could not be saved");
        setEr("error");
      }, 600);
    } else if (typeof onImportComplete === "function" && failedCount > 0) {
      onImportComplete();
    }
  };

  const percent =
    progress.total > 0
      ? Math.round((progress.processed / progress.total) * 100)
      : 0;

  const summary = preview?.summary;

  return (
    <>
      {isLoading && <Loader />}
      <div
        className="import-inventory-modal-backdrop"
        onClick={() => !progress.running && toggle()}
      >
        <div
          className="import-inventory-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="header">
            <h2>Import Inventory (Excel)</h2>
            <button
              className="close"
              onClick={() => !progress.running && toggle()}
            >
              ✕
            </button>
          </div>

          <div className="body">
            <div className="section">
              <div className="file-row">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xls,.xlsx"
                  style={{ display: "none" }}
                  onChange={onFileChange}
                />
                <button
                  className="primary"
                  onClick={onPickFile}
                  disabled={progress.running}
                >
                  Select Excel file
                </button>
                <div className="file-name">
                  {fileName ? fileName : "No file selected"}
                </div>
              </div>

              <div className="toggles">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={autoCreateMissing}
                    onChange={(e) => setAutoCreateMissing(e.target.checked)}
                    disabled={progress.running}
                  />
                  <span>Auto-create missing Brand & Category</span>
                </label>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={prefixBrandToName}
                    onChange={(e) => setPrefixBrandToName(e.target.checked)}
                    disabled={progress.running}
                  />
                  <span>Prefix Brand to Product Name</span>
                </label>
              </div>
            </div>

            {summary && (
              <div className="section meta">
                <div className="small-title">Validation summary</div>
                <div className="meta-grid">
                  <div className="meta-item">
                    <div className="label">Will create</div>
                    <div className="value">{summary.toCreate}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Will update</div>
                    <div className="value">{summary.toUpdate}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Brands to create</div>
                    <div className="value">{summary.brandsToCreate}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Categories to create</div>
                    <div className="value">{summary.categoriesToCreate}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Errors</div>
                    <div className="value">{summary.errors}</div>
                  </div>
                  <div className="meta-item">
                    <div className="label">Total rows</div>
                    <div className="value">{summary.totalRows}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="section meta">
              <div className="meta-grid">
                <div className="meta-item">
                  <div className="label">Rows detected</div>
                  <div className="value">{totalRows}</div>
                </div>
                <div className="meta-item">
                  <div className="label">Progress</div>
                  <div className="value">
                    {progress.running
                      ? `${progress.processed}/${progress.total} (${percent}%)`
                      : progress.total
                      ? `${progress.created} created / ${progress.updated} updated`
                      : "-"}
                  </div>
                </div>
                <div className="meta-item">
                  <div className="label">Created / Updated / Failed</div>
                  <div className="value">
                    {progress.created} / {progress.updated} / {progress.failed}
                  </div>
                </div>
              </div>

              {progress.total > 0 && (
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${percent}%` }} />
                </div>
              )}
            </div>

            {preview?.planRows?.length > 0 && (
              <div className="section">
                <div className="small-title">Row review</div>
                <div className="review-table-wrap">
                  <table className="review-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Action</th>
                        <th>Product</th>
                        <th>Brand</th>
                        <th>Category</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.planRows.slice(0, 300).map((r) => (
                        <tr key={`${r.excelRowNumber}-${r.action}`} className={r.action}>
                          <td>{r.excelRowNumber}</td>
                          <td>
                            <span className={`badge ${r.action}`}>{r.action}</span>
                          </td>
                          <td>{r.finalName || r.productName}</td>
                          <td>{r.brand}</td>
                          <td>{r.category}</td>
                          <td>{r.error || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.planRows.length > 300 && (
                  <div className="hint">Showing first 300 of {preview.planRows.length} rows.</div>
                )}
              </div>
            )}

            {detectedHeaders?.length > 0 && (
              <div className="section">
                <div className="small-title">Detected columns (first sheet)</div>
                <div className="chips">
                  {detectedHeaders.map((h) => (
                    <span key={h} className="chip">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="section">
              <div className="small-title">Import log</div>
              <div className="log">
                {logs.length === 0 ? (
                  <div className="log-empty">No logs yet.</div>
                ) : (
                  logs.slice(-200).map((l) => (
                    <div
                      key={`${l.ts}-${l.rowNumber}-${l.type}`}
                      className={`log-row ${l.type}`}
                    >
                      <div className="row-no">
                        {l.rowNumber ? `Row ${l.rowNumber}` : "File"}
                      </div>
                      <div className="msg">{l.message}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="footer">
            <button
              className="secondary"
              onClick={() => !progress.running && toggle()}
            >
              Cancel
            </button>
            <button
              className="secondary"
              onClick={onValidateClick}
              disabled={!canValidate}
            >
              Validate
            </button>
            <button
              className="primary"
              onClick={startImport}
              disabled={!canConfirm && !progress.running}
            >
              {progress.running ? "Importing..." : "Confirm import"}
            </button>
          </div>
        </div>
      </div>

      {success && <Success modal={success} toastmsg={toastmsg} er={er} />}
    </>
  );
}
